import React from "react";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import { ShieldCheck, Fingerprint, Cable, KeyRound } from "lucide-react";
import PartnerLink, { AutoLinkPartners } from "@/components/marketing/PartnerLink";

const ITEMS = [
  {
    icon: Cable,
    title: "Mastercard Finicity Integration",
    partner: "finicity",
    body: "Bank connectivity handled by one of the world's leading open-banking providers.",
  },
  {
    icon: ShieldCheck,
    title: "Bank-Level Security",
    body: "Segregated storage, hardened infrastructure, and continuous monitoring.",
  },
  {
    icon: Fingerprint,
    title: "Encrypted API Connections",
    body: "All service-to-service traffic is authenticated and encrypted end-to-end.",
  },
  {
    icon: KeyRound,
    title: "Credentials Never Stored",
    body: "ZenMe never asks for online banking credentials or stores your bank passwords.",
  },
];

export const SecuritySection = () => {
  return (
    <div className="relative overflow-hidden" data-testid="security-section">
      <GlowOrb
        color="emerald"
        size={480}
        intensity={0.1}
        className="bottom-0 right-[-100px]"
        animate={false}
      />

      <Section
        eyebrow="Security & Trust"
        headline="Built on trusted financial infrastructure."
        subheadline={
          <>
            ZenMe never asks for online banking credentials directly. All bank
            connections are securely facilitated through{" "}
            <PartnerLink partner="finicity" showGlyph={false} className="!text-inherit">
              Mastercard Finicity
            </PartnerLink>{" "}
            — one of the world&rsquo;s leading open banking providers. Your
            financial data is encrypted end-to-end and used solely to generate
            funding insights.
          </>
        }
        spacing="lg"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <FadeIn key={it.title} delay={0.05 * i}>
                <Card
                  variant="elevated"
                  className="group flex h-full flex-col gap-4 p-6"
                  data-testid={`security-item-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_18px_rgba(0,230,118,0.4)]">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h4 className="font-display text-base font-semibold text-white">
                      {it.partner ? (
                        <PartnerLink partner={it.partner} showGlyph={false} className="!text-white hover:!text-brand-emerald">
                          {it.title}
                        </PartnerLink>
                      ) : (
                        it.title
                      )}
                    </h4>
                    <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">
                      <AutoLinkPartners text={it.body} />
                    </p>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>
    </div>
  );
};

export default SecuritySection;
